/* eslint-disable no-shadow */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import PlayerCardV3 from '../../../../../components/cards/PlayerCardV3';
import Rating from '../../../../../components/cards/PlayerCard/SharedComponents/Rating';
import { sportTypeIds, utrTypeIds, registrationGroupStatusTypeIds, drawCategoryTypeIds } from '../../../../../utils/eventConstants';
import { getPlayerPaymentStatus, getPlayerRegStatusV3, getPlayerPosition, getPlayerRegDateV3, getDrawStartTimeV3 } from '../../../../../utils';
import { gameTypes } from '../../../../../utils/constants';
import AvatarV2 from '../../../../../components/cards/PlayerCard/ListViewComponents/AvatarV2';
import PlayerDetails from '../../../../../components/cards/PlayerCard/ListViewComponents/PlayerDetails';
import { getPlayerCardPlayerDetails } from '../../../../../utils/formatting';
import Img from '../../../../../components/Img';
import * as eventProfileActions from '../../../../../actions/event-profile-actions';
import { divisionShape, playerShape, playersDictByMemberIdShape, drawShape, timeZoneShape, menuOptionShape } from '../../../../../propTypes';

const Division = ({ division, onPlayerDivisionClicked }) => {
    const name = division?.name?.charAt(0)?.toUpperCase() + division?.name?.slice(1);

    const onClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        onPlayerDivisionClicked(e, division);
    }, [division, onPlayerDivisionClicked]);

    return (
        <div
            className={`values ellipsis link`}
            title={name}
            key={division.name}
            onClick={onClick}
        >
            {name}
        </div>
    );
};

Division.propTypes = {
    onPlayerDivisionClicked: PropTypes.func.isRequired,
    division: divisionShape.isRequired,
};

const AllDivisionsPlayerCard = props => {
    const {
        gridLayout,
        hideRatings,
        hideUtrLabel,
        isCurrentMemberAdmin,
        player,
        playersDictByMemberId,
        showDecimals,
        showHighSchoolName,
        showPartner,
        showPickleBallRatings = true,
        showRatingNotLoggedIn,
        showRegStatus,
        showTennisRatings = true,
        sportTypeId,
        utrTypeId,
        showPlayers,
        showNeedPartners,
        drawsDict = {},
        handleTabClick,
        timeZone,
        showDivisions,
        showDraws,
        eventId,
        isEventStarted,
        onClickPlayerDivision,
    } = props;

    const dispatch = useDispatch();

    const auth = useSelector(({ auth }) => auth);
    const user = auth?.user;

    const { firstName, lastName, nationality, playerProfileImages, ratingStatusSingles, ratingStatusDoubles, playerId } = player;

    const descriptionItems = getPlayerCardPlayerDetails(player, isCurrentMemberAdmin, showHighSchoolName);

    const filteredDivisions = (player.divisions || []).filter(div => {
        if (!showPlayers && showNeedPartners && player.memberId !== auth.user?.id) {
            return div.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER && !(div.isEventEntryFeeDivision || div.name === 'Player Entry Fee');
        }
        return !(div.isEventEntryFeeDivision || div.name === 'Player Entry Fee');
    });

    const regStatuses = filteredDivisions?.map(div => getPlayerRegStatusV3(div));
    const regDates = filteredDivisions?.map(div => getPlayerRegDateV3(div, timeZone?.value));
    const paymentStatuses = filteredDivisions?.map(div => getPlayerPaymentStatus(div.paymentStatusTypeId));

    const partnerNames = filteredDivisions?.map(div => {
        if (div.partners.length) {
            return `${playersDictByMemberId[div.partners[0]]?.firstName[0]} ${playersDictByMemberId[div.partners[0]]?.lastName}`;
        }
        return '-';
    });

    const positions = filteredDivisions?.map(div => getPlayerPosition(div));

    const playerDraws = filteredDivisions?.map(div => {
        const associatedDraw = player.draws.find(draw => draw.id === div.associatedDraw);
        if (associatedDraw) {
            return {
                ...associatedDraw,
                ...drawsDict[associatedDraw.id],
                name: associatedDraw.name,
            };
        }
        return null;
    }).filter(draw => {
        return showDivisions ? true : !!draw;
    });

    const handleDrawClick = (e, drawId) => {
        const drawsTabId = 4;
        e.stopPropagation();
        e.preventDefault();
        const drawToLoad = drawsDict[drawId];
        // load draws before redirect
        if (!drawToLoad.parentDrawId) {
            dispatch(eventProfileActions.getDraw({ eventId, drawId: drawToLoad.id, setAsCurrentDraw: true }));
        } else {
            dispatch(eventProfileActions.getDraw({ eventId, drawId: drawToLoad.parentDrawId, dontAutoLoadFirstChildDraw: true }));
            dispatch(eventProfileActions.getDraw({ eventId, drawId: drawToLoad.id, isChildDraw: true }));
        }
        handleTabClick({ tabId: drawsTabId, drawId });
    };

    const getDrawName = (draw) => {
        if (!draw) {
            return '-';
        }
        if (draw.isGroupedChildDraw && draw.parentDrawName) {
            return `${draw.parentDrawName}, ${draw.name}`;
        }
        if (draw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW_QUALIFIER) {
            return `${draw.parentDrawName} - ${draw.name}`;
        }
        return draw.name;
    };

    const getDrawPartnerName = (memberId) => {
        if (memberId) {
            return `${playersDictByMemberId[memberId]?.firstName[0]} ${playersDictByMemberId[memberId]?.lastName}`;
        }
        return '-';
    };

    const getDrawStatus = (draw) => {
        if (!isEventStarted || !draw) {
            return <>-</>;
        }
        return <>{draw.drawStatus} {(draw.medal && draw.medal < 4) ? <Img src={`/img/medals/${draw.medal}.svg`} style={{ height: '17px', marginTop: '2px', marginBottom: '2px' }} /> : null}</>;
    };

    const getPlayerStatus = (draw) => {
        const notStarted = 'Not Started';
        if (!draw) {
            return '-';
        }
        if (draw?.drawStatus === notStarted) {
            return getDrawStartTimeV3(draw, timeZone?.value);
        }
        return draw.playerStatus;
    };

    const onPlayerDivisionClicked = (e, division) => {
        e.preventDefault();
        e.stopPropagation();
        onClickPlayerDivision(division);
    };

    return (
        <PlayerCardV3 {...props}>
            <div className={`col-12`}>
                <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.join(' ') }}>
                    <div className={`d-flex aic oh pr8`}>
                        <AvatarV2 playerImages={playerProfileImages} nationality={nationality} player={player} />
                        <PlayerDetails
                            firstName={firstName}
                            lastName={lastName}
                            description={descriptionItems}
                            playerId={playerId}
                            isCurrentMemberAdmin={isCurrentMemberAdmin}
                        />
                    </div>
                    {!hideRatings && (
                        <>
                            {showTennisRatings && (
                                <>
                                    <div className={`d-flex aic pr8`}>
                                        {(user || showRatingNotLoggedIn) && (
                                            <Rating
                                                utrType={utrTypeId}
                                                ratingType={gameTypes.SINGLES}
                                                ratingStatus={ratingStatusSingles}
                                                player={player}
                                                showDecimals={showDecimals}
                                                hideUtrLabel={hideUtrLabel}
                                                sportTypeId={sportTypeId}
                                            />
                                        )}
                                    </div>
                                    <div className={`d-flex aic pr8`}>
                                        {(user || showRatingNotLoggedIn) && (
                                            <Rating
                                                utrType={utrTypeId}
                                                ratingType={gameTypes.DOUBLES}
                                                ratingStatus={ratingStatusDoubles}
                                                player={player}
                                                showDecimals={showDecimals}
                                                hideUtrLabel={hideUtrLabel}
                                                sportTypeId={sportTypeId}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                            {showPickleBallRatings && (
                                <>
                                    <div className={`d-flex aic pr8`}>
                                        {(user || showRatingNotLoggedIn) && (
                                            <Rating
                                                utrType={utrTypeId}
                                                ratingType={gameTypes.SINGLES}
                                                player={player}
                                                showDecimals={showDecimals}
                                                hideUtrLabel={hideUtrLabel}
                                                sportTypeId={sportTypeId}
                                                isPickleballRating
                                            />
                                        )}
                                    </div>
                                    <div className={`d-flex aic pr8`}>
                                        {(user || showRatingNotLoggedIn) && (
                                            <Rating
                                                utrType={utrTypeId}
                                                ratingType={gameTypes.DOUBLES}
                                                player={player}
                                                showDecimals={showDecimals}
                                                hideUtrLabel={hideUtrLabel}
                                                sportTypeId={sportTypeId}
                                                isPickleballRating
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {showDivisions && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {filteredDivisions.map(div => {
                                return (
                                    <Division
                                        key={div.id}
                                        division={div}
                                        onPlayerDivisionClicked={onPlayerDivisionClicked}
                                    />
                                );
                            })}
                        </div>
                    )}
                    {showDivisions && isCurrentMemberAdmin && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {regDates.map((regDate, i) => (
                                <div className={`values ellipsis`} title={regDate} key={`${regDate}-${i}`}>
                                    {regDate}
                                </div>
                            ))}
                        </div>
                    )}
                    {showDivisions && showRegStatus && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {regStatuses.map((rs, i) => (
                                <div className={`values ${rs.color} ellipsis`} title={rs.title} key={`${rs.label}-${i}`}>
                                    {rs.label}
                                </div>
                            ))}
                        </div>
                    )}
                    {showDivisions && showPartner && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {partnerNames.map((name, i) => (
                                <div className={`values ellipsis`} title={name} key={`${name}-${i}`}>
                                    {name}
                                </div>
                            ))}
                        </div>
                    )}
                    {showDivisions && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {positions.map((pos, i) => (
                                <div className={`values ellipsis`} title={pos?.bucket} key={`${pos?.bucket}-${i}`}>
                                    {pos?.bucket}
                                </div>
                            ))}
                        </div>
                    )}
                    {showDivisions && isCurrentMemberAdmin && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {paymentStatuses.map((ps, i) => (
                                <div className={`values ${ps.color} ellipsis`} title={ps.label} key={`${ps.label}-${i}`}>
                                    {ps.label}
                                </div>
                            ))}
                        </div>
                    )}
                    {showDraws && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {playerDraws.map((draw, i) => (
                                <div className={`values ellipsis`} title={getDrawName(draw)} key={draw?.id || `draw-${i}`}>
                                    {draw && (
                                        <div
                                            className={`${draw.isPublished ? 'link' : ''}`}
                                            onClick={draw.isPublished ? (e) => handleDrawClick(e, draw.id) : null}
                                        >
                                            {getDrawName(draw)}
                                        </div>
                                    )}
                                    {!draw && <>-</>}
                                </div>
                            ))}
                        </div>
                    )}
                    {showDraws && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {playerDraws.map((draw, i) => {
                                const drawPartner = getDrawPartnerName(draw?.drawPartner);
                                return (
                                    <div className={`values ellipsis`} title={drawPartner} key={`st-${draw?.id || i}`}>
                                        {drawPartner}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {showDraws && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {playerDraws.map((draw, i) => (
                                <div className={`values ellipsis`} title={getDrawStatus(draw)} key={`st-${draw?.id || i}`}>
                                    {getDrawStatus(draw)}
                                </div>
                            ))}
                        </div>
                    )}
                    {showDraws && (
                        <div className={`d-flex flex-column jcc oh pr8`}>
                            {playerDraws.map((draw, i) => (
                                <div className={`values ellipsis`} title={getPlayerStatus(draw)} key={`plst-${draw?.id || i}`}>
                                    {getPlayerStatus(draw)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </PlayerCardV3>
    );
};

AllDivisionsPlayerCard.propTypes = {
    addOptionsSpace: PropTypes.bool,
    gridLayout: PropTypes.arrayOf(PropTypes.string).isRequired,
    hideRatings: PropTypes.bool,
    hideUtrLabel: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    isSelected: PropTypes.bool,
    menuOptions: PropTypes.arrayOf(menuOptionShape),
    onMenuSelected: PropTypes.func,
    onSelectClick: PropTypes.func,
    handleTabClick: PropTypes.func,
    player: playerShape.isRequired,
    playersDictByMemberId: playersDictByMemberIdShape.isRequired,
    sendMessage: PropTypes.func,
    showCheckbox: PropTypes.bool,
    showDecimals: PropTypes.bool,
    showHighSchoolName: PropTypes.bool,
    showPartner: PropTypes.bool,
    showPickleBallRatings: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    showRegStatus: PropTypes.bool,
    showTennisRatings: PropTypes.bool,
    sportTypeId: PropTypes.number,
    utrTypeId: PropTypes.number,
    showPlayers: PropTypes.bool,
    showNeedPartners: PropTypes.bool,
    drawsDict: PropTypes.objectOf(drawShape),
    dataType: PropTypes.string,
    timeZone: timeZoneShape,
    showDraws: PropTypes.bool,
    showDivisions: PropTypes.bool,
    isEventStarted: PropTypes.bool,
    onClickPlayerDivision: PropTypes.func.isRequired,
};

AllDivisionsPlayerCard.defaultProps = {
    addOptionsSpace: true,
    hideRatings: false,
    hideUtrLabel: false,
    isCurrentMemberAdmin: false,
    isSelected: false,
    menuOptions: null,
    onMenuSelected: () => { },
    onSelectClick: () => { },
    handleTabClick: () => { },
    sendMessage: () => { },
    showDecimals: false,
    showCheckbox: false,
    showHighSchoolName: false,
    showPartner: false,
    showPickleBallRatings: false,
    showRatingNotLoggedIn: false,
    showRegStatus: false,
    showTennisRatings: false,
    sportTypeId: sportTypeIds.TENNIS,
    utrTypeId: utrTypeIds.MYUTR,
    showPlayers: false,
    showNeedPartners: false,
    drawsDict: {},
    dataType: '',
    timeZone: null,
    showDraws: false,
    showDivisions: false,
    isEventStarted: false,
};

export default withRouter(AllDivisionsPlayerCard);
